import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pie, Line } from "react-chartjs-2";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { usePortalScope } from "../context/PortalScopeContext";
import "../styles/Dashboard.css";

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartDataLabels
);

// Cohesive, user-friendly color palettes (brand orange + complementary)
const CHART_COLORS_SOURCES = [
  "#f68b1f",
  "#0d6efd",
  "#198754",
  "#6f42c1",
  "#0dcaf0",
  "#fd7e14",
  "#20c997",
  "#ffc107",
  "#dc3545",
  "#6c757d",
];

const CHART_COLORS_COUNTRIES = [
  "#c95f00",
  "#0ea5e9",
  "#10b981",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#06b6d4",
  "#84cc16",
  "#ef4444",
  "#64748b",
];

const CHART_FONT_FAMILY = "'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif";

const pieOptionsBase = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: 16 },
  elements: { arc: { borderRadius: 6 } },
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        padding: 14,
        font: { size: 12, family: CHART_FONT_FAMILY, weight: "500" },
        usePointStyle: true,
        pointStyle: "circle",
      },
    },
    tooltip: {
      backgroundColor: "rgba(26, 29, 33, 0.95)",
      titleFont: { size: 13, weight: "600" },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct = total ? ((ctx.raw / total) * 100).toFixed(1) : 0;
          return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
        },
      },
    },
    datalabels: {
      color: "#fff",
      font: { size: 12, weight: "600", family: CHART_FONT_FAMILY },
      formatter: (value, ctx) => {
        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
        const pct = total ? ((value / total) * 100).toFixed(0) : 0;
        return pct > 8 ? `${pct}%` : "";
      },
      padding: 4,
      textShadow: true,
    },
  },
};

const lineOptionsBase = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 12, right: 12, bottom: 0, left: 0 } },
  plugins: {
    legend: {
      display: true,
      position: "top",
      align: "end",
      labels: {
        padding: 12,
        font: { size: 12, family: CHART_FONT_FAMILY, weight: "500" },
        usePointStyle: true,
      },
    },
    tooltip: {
      backgroundColor: "rgba(26, 29, 33, 0.95)",
      titleFont: { size: 13, weight: "600" },
      bodyFont: { size: 12 },
      padding: 12,
      cornerRadius: 8,
      mode: "index",
      intersect: false,
    },
    datalabels: { display: false },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { size: 11, family: CHART_FONT_FAMILY },
        maxRotation: 45,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      grid: { color: "rgba(0,0,0,0.06)" },
      ticks: {
        font: { size: 11, family: CHART_FONT_FAMILY },
        stepSize: 1,
      },
    },
  },
};

const Dashboard = () => {
  const [vendorData, setVendorData] = useState([]);
  const api_url = process.env.REACT_APP_API_URL;
  const { scopeParam, canSwitchPortal, portalScope } = usePortalScope();

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("No token found. User may not be logged in.");
          return;
        }

        const response = await axios.get(`${api_url}/api/vendor${scopeParam}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setVendorData(response.data);
      } catch (error) {
        console.error("Error fetching vendor data:", error);
      }
    };

    fetchVendors();
  }, [api_url, scopeParam]);

  // Total customer count
  const totalCustomers = vendorData.length;

  // Count categories
  const categoryCounts = vendorData.reduce((acc, vendor) => {
    acc[vendor.CategoryName] = (acc[vendor.CategoryName] || 0) + 1;
    return acc;
  }, {});

  // Count sources
  const sourceCounts = vendorData.reduce((acc, vendor) => {
    acc[vendor.SourceName] = (acc[vendor.SourceName] || 0) + 1;
    return acc;
  }, {});

  // Count countries
  const countryCounts = vendorData.reduce((acc, vendor) => {
    acc[vendor.Country] = (acc[vendor.Country] || 0) + 1;
    return acc;
  }, {});

  // Count shipped/dispatched over time
  const shippedCounts = vendorData
    .filter((vendor) => vendor.Status === "Shipped / Dispatched")
    .reduce((acc, vendor) => {
      const month = new Date(vendor.Date).toLocaleString("default", {
        month: "short",
      });
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {});

  const sourceLabels = Object.keys(sourceCounts);
  const countryLabels = Object.keys(countryCounts);
  const sourceValues = Object.values(sourceCounts);
  const countryValues = Object.values(countryCounts);

  const pickColors = (palette, n) =>
    Array.from({ length: n }, (_, i) => palette[i % palette.length]);

  // Pie Chart (SourceName) — rounded segments, clear labels
  const pieDataSource = {
    labels: sourceLabels,
    datasets: [
      {
        data: sourceValues,
        backgroundColor: pickColors(CHART_COLORS_SOURCES, sourceLabels.length),
        borderColor: "#fff",
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  // Pie Chart (Country)
  const pieDataCountry = {
    labels: countryLabels,
    datasets: [
      {
        data: countryValues,
        backgroundColor: pickColors(CHART_COLORS_COUNTRIES, countryLabels.length),
        borderColor: "#fff",
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const growthLabels = Object.keys(shippedCounts);
  const growthValues = Object.values(shippedCounts);

  // Line Chart (Shipped/Dispatched) — smooth curve, gradient fill, visible points
  const lineDataGrowth = {
    labels: growthLabels.length > 0 ? growthLabels : ["No data yet"],
    datasets: [
      {
        label: "Shipped / Dispatched",
        data: growthValues.length > 0 ? growthValues : [0],
        borderColor: "#f68b1f",
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return "rgba(246, 139, 31, 0.15)";
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(246, 139, 31, 0.35)");
          gradient.addColorStop(1, "rgba(246, 139, 31, 0.02)");
          return gradient;
        },
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
        pointBackgroundColor: "#f68b1f",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const pieOptionsSource = { ...pieOptionsBase };
  const pieOptionsCountry = { ...pieOptionsBase };
  const lineOptionsGrowth = { ...lineOptionsBase };

  return (
    <div className="dashboard-wrap">
      <div className="container">
        <div className="dashboard-header">
          {canSwitchPortal && (
            <div className="dashboard-badge-wrap">
              <span className="badge bg-primary px-3 py-2">
                Viewing: {portalScope} portal
              </span>
            </div>
          )}
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">
            Overview of vendors and activity
          </p>
        </div>

        {/* Total Customers — hero stat */}
        <div className="row mb-3 mb-md-4">
          <div className="col-12">
            <div className="dashboard-hero-card d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <div className="dashboard-hero-label">Total Customers</div>
                <div className="dashboard-hero-value">{totalCustomers}</div>
              </div>
              <div className="dashboard-hero-icon">
                <i className="bi bi-people-fill" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>

        {/* Category counts — responsive grid */}
        {Object.keys(categoryCounts).length > 0 && (
          <div className="row g-3 mb-3 mb-md-4">
            {Object.keys(categoryCounts).map((category, index) => (
              <div
                key={index}
                className="col-6 col-sm-6 col-md-4 col-lg-3"
              >
                <div className="dashboard-stat-card text-center">
                  <div className="dashboard-stat-label">{category}</div>
                  <div className="dashboard-stat-value">
                    {categoryCounts[category]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Charts section */}
        <h2 className="dashboard-section-title">
          <i className="bi bi-bar-chart-line me-2" aria-hidden="true" />
          Analytics
        </h2>
        <div className="row g-3 g-md-4">
          <div className="col-12 col-lg-4">
            <div className="dashboard-chart-card dashboard-chart-card--source">
              <h3 className="dashboard-chart-title">
                <i className="bi bi-diagram-3 me-2" aria-hidden="true" />
                Source Diversity
              </h3>
              <div className="dashboard-chart-wrap">
                <Pie
                  data={pieDataSource}
                  options={pieOptionsSource}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="dashboard-chart-card dashboard-chart-card--country">
              <h3 className="dashboard-chart-title">
                <i className="bi bi-globe me-2" aria-hidden="true" />
                Country Diversity
              </h3>
              <div className="dashboard-chart-wrap">
                <Pie
                  data={pieDataCountry}
                  options={pieOptionsCountry}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4">
            <div className="dashboard-chart-card dashboard-chart-card--growth">
              <h3 className="dashboard-chart-title">
                <i className="bi bi-graph-up-arrow me-2" aria-hidden="true" />
                Growth (Shipped / Dispatched)
              </h3>
              <div className="dashboard-chart-wrap">
                <Line data={lineDataGrowth} options={lineOptionsGrowth} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
